import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import * as tenantService from '../../services/tenantService';
import PageHeader from '../shell/PageHeader';
import { DataTable, FilterBar, Field, Modal, Menu, MenuItem, Notice, SaveBar } from '../patterns';
import type { DataTableColumn } from '../patterns';
import PromoteFromTenantModal from './PromoteFromTenantModal';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GlobalPest {
  _id: string;
  code: string;
  name: string;
  aliases: string[];
  measurementType: 'percentage' | 'level' | 'count';
}

type MeasurementType = GlobalPest['measurementType'];

// ─── PestEditModal ────────────────────────────────────────────────────────────

interface PestEditModalProps {
  show: boolean;
  pest: Partial<GlobalPest> | null;
  onHide: () => void;
  onSaved: () => void;
}

const PestEditModal: React.FC<PestEditModalProps> = ({ show, pest, onHide, onSaved }) => {
  const { t } = useTranslation();
  const isEditing = !!(pest && pest._id);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [aliases, setAliases] = useState('');
  const [measurementType, setMeasurementType] = useState<MeasurementType>('percentage');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync form state when pest changes
  useEffect(() => {
    if (pest) {
      setCode(pest.code || '');
      setName(pest.name || '');
      setAliases((pest.aliases || []).join(', '));
      setMeasurementType(pest.measurementType || 'percentage');
    } else {
      setCode('');
      setName('');
      setAliases('');
      setMeasurementType('percentage');
    }
    setError(null);
  }, [pest, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const payload = {
        code: code.toUpperCase(),
        name,
        aliases: aliases.split(',').map(s => s.trim()).filter(Boolean),
        measurementType,
      };
      if (isEditing && pest?._id) {
        await axios.put(`/api/v1/global-pests/${pest._id}`, payload, { withCredentials: true });
      } else {
        await axios.post('/api/v1/global-pests', payload, { withCredentials: true });
      }
      onSaved();
      onHide();
    } catch (err: any) {
      setError(err.response?.data?.error || t('system.pests.errors.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const title = isEditing
    ? t('system.pests.modal.editTitle')
    : t('system.pests.modal.createTitle');

  return (
    <Modal show={show} onHide={onHide} title={title}>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Notice tone="danger" className="mb-3">{error}</Notice>
          )}

          <Field
            label={t('system.pests.modal.fields.code')}
            controlId="pestEditCode"
            required
          >
            <input
              id="pestEditCode"
              type="text"
              className="pat-input"
              value={code}
              required
              disabled={isEditing}
              maxLength={3}
              onChange={e =>
                setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3))
              }
              aria-label={t('system.pests.modal.fields.code')}
            />
          </Field>

          <Field
            label={t('system.pests.modal.fields.name')}
            controlId="pestEditName"
            required
          >
            <input
              id="pestEditName"
              type="text"
              className="pat-input"
              value={name}
              required
              onChange={e => setName(e.target.value)}
              aria-label={t('system.pests.modal.fields.name')}
            />
          </Field>

          <Field
            label={t('system.pests.modal.fields.aliases')}
            controlId="pestEditAliases"
          >
            <input
              id="pestEditAliases"
              type="text"
              className="pat-input"
              value={aliases}
              onChange={e => setAliases(e.target.value)}
              aria-label={t('system.pests.modal.fields.aliases')}
            />
          </Field>

          <Field
            label={t('system.pests.modal.fields.measurementType')}
            controlId="pestEditMeasurementType"
            required
          >
            <select
              id="pestEditMeasurementType"
              className="pat-input"
              value={measurementType}
              onChange={e => setMeasurementType(e.target.value as MeasurementType)}
              aria-label={t('system.pests.modal.fields.measurementType')}
            >
              <option value="percentage">Percentage</option>
              <option value="level">Level</option>
              <option value="count">Count</option>
            </select>
          </Field>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={onHide}
          >
            {t('system.pests.modal.cancel')}
          </button>
          <button
            type="submit"
            className="pat-btn pat-btn--primary"
            disabled={loading}
          >
            {t('system.pests.modal.save')}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

// ─── SystemPestsPage ──────────────────────────────────────────────────────────

const SystemPestsPage: React.FC = () => {
  const { t } = useTranslation();

  useBreadcrumb([
    { label: t('nav.system.module'), to: '/system' },
    { label: t('system.pests.title') },
  ]);

  // ─── Core data state ──────────────────────────────────────────────────────

  const [pests, setPests] = useState<GlobalPest[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  // ─── Inline-edit dirty state ──────────────────────────────────────────────

  const [dirtyPests, setDirtyPests] = useState<Record<string, Partial<GlobalPest>>>({});
  const hasDirtyChanges = Object.keys(dirtyPests).length > 0;

  // ─── Bulk-select state ────────────────────────────────────────────────────

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // ─── Modal state ──────────────────────────────────────────────────────────

  const [showEditModal, setShowEditModal] = useState(false);
  const [editing, setEditing] = useState<Partial<GlobalPest> | null>(null);
  const [showPromoteModal, setShowPromoteModal] = useState(false);

  // ─── Data fetching ────────────────────────────────────────────────────────

  const fetchGlobalPests = useCallback(async () => {
    try {
      const res = await axios.get('/api/v1/global-pests', { withCredentials: true });
      setPests(res.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || t('system.pests.errors.loadFailed'));
    }
  }, [t]);

  const fetchTenants = useCallback(async () => {
    try {
      const data = await tenantService.listTenants();
      setTenants(data);
    } catch {
      // Non-critical
    }
  }, []);

  const fetchAll = useCallback(async () => {
    await Promise.all([fetchGlobalPests(), fetchTenants()]);
  }, [fetchGlobalPests, fetchTenants]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ─── Filtered rows ────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    if (!search.trim()) return pests;
    const q = search.toLowerCase();
    return pests.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.code.toLowerCase().includes(q) ||
      (p.aliases || []).some(a => a.toLowerCase().includes(q))
    );
  }, [pests, search]);

  // ─── Effective row helper (dirty-state overlay) ───────────────────────────

  const effective = (p: GlobalPest): GlobalPest => ({ ...p, ...(dirtyPests[p._id] || {}) });

  // ─── Inline-edit handlers ─────────────────────────────────────────────────

  const markDirty = (pestId: string, changes: Partial<GlobalPest>) => {
    setDirtyPests(prev => ({
      ...prev,
      [pestId]: { ...(prev[pestId] || {}), ...changes },
    }));
  };

  const handleSaveAll = async () => {
    try {
      const updates = Object.entries(dirtyPests).map(([id, changes]) => {
        const original = pests.find(p => p._id === id)!;
        const merged = { ...original, ...changes };
        return axios.put(`/api/v1/global-pests/${id}`, merged, { withCredentials: true });
      });
      await Promise.all(updates);
      setDirtyPests({});
      await fetchGlobalPests();
    } catch (err: any) {
      setError(err.response?.data?.error || t('system.pests.errors.saveFailed'));
    }
  };

  const handleDiscard = () => {
    setDirtyPests({});
  };

  // ─── Bulk-select handlers ─────────────────────────────────────────────────

  const toggleSelectionMode = () => {
    setSelectionMode(prev => !prev);
    setSelectedIds(new Set());
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(p => p._id)));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(t('system.pests.deleteConfirm'))) return;
    try {
      await Promise.all(
        Array.from(selectedIds).map(id =>
          axios.delete(`/api/v1/global-pests/${id}`, { withCredentials: true })
        )
      );
      setSelectedIds(new Set());
      setSelectionMode(false);
      await fetchGlobalPests();
    } catch (err: any) {
      setError(err.response?.data?.error || t('system.pests.errors.deleteFailed'));
    }
  };

  // ─── Modal handlers ───────────────────────────────────────────────────────

  const handleOpenCreate = () => {
    setEditing(null);
    setShowEditModal(true);
  };

  const handleOpenEdit = (pest: GlobalPest) => {
    setEditing({ ...pest });
    setShowEditModal(true);
  };

  const handleCloseEdit = () => {
    setShowEditModal(false);
    setEditing(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('system.pests.deleteConfirm'))) return;
    try {
      await axios.delete(`/api/v1/global-pests/${id}`, { withCredentials: true });
      await fetchGlobalPests();
    } catch (err: any) {
      setError(err.response?.data?.error || t('system.pests.errors.deleteFailed'));
    }
  };

  // ─── DataTable columns ────────────────────────────────────────────────────

  const columns: DataTableColumn<GlobalPest>[] = [
    // Optional: select checkbox column (only when selectionMode)
    ...(selectionMode ? [{
      key: '__select',
      label: (
        <input
          type="checkbox"
          checked={selectedIds.size === filtered.length && filtered.length > 0}
          onChange={toggleSelectAll}
          aria-label="Select all pests"
        />
      ) as React.ReactNode,
      render: (p: GlobalPest) => (
        <input
          type="checkbox"
          checked={selectedIds.has(p._id)}
          onChange={() => toggleSelect(p._id)}
          onClick={e => e.stopPropagation()}
          aria-label={`Select ${p.name}`}
        />
      ),
    }] as DataTableColumn<GlobalPest>[] : []),

    {
      key: 'code',
      label: t('system.pests.columns.code'),
      sortable: true,
      render: p => {
        const row = effective(p);
        const isDirty = !!dirtyPests[p._id];
        return (
          <input
            type="text"
            className={`pat-input${isDirty ? ' pat-input--dirty' : ''}`}
            value={row.code}
            maxLength={3}
            onChange={e =>
              markDirty(p._id, {
                code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3),
              })
            }
            aria-label={`Code for ${p.name}`}
            style={{ minWidth: '60px', maxWidth: '80px' }}
          />
        );
      },
    },

    {
      key: 'name',
      label: t('system.pests.columns.name'),
      sortable: true,
      render: p => {
        const row = effective(p);
        return (
          <input
            type="text"
            className="pat-input"
            value={row.name}
            onChange={e => markDirty(p._id, { name: e.target.value })}
            aria-label={`Name for ${p.code}`}
            style={{ minWidth: '140px' }}
          />
        );
      },
    },

    {
      key: 'aliases',
      label: t('system.pests.columns.aliases'),
      render: p => {
        const row = effective(p);
        return (
          <input
            type="text"
            className="pat-input"
            value={(row.aliases || []).join(', ')}
            onChange={e =>
              markDirty(p._id, {
                aliases: e.target.value
                  .split(',')
                  .map(s => s.trim())
                  .filter(Boolean),
              })
            }
            aria-label={`Aliases for ${p.code}`}
            style={{ minWidth: '120px' }}
          />
        );
      },
    },

    {
      key: 'measurementType',
      label: t('system.pests.columns.measurementType'),
      sortable: true,
      render: p => {
        const row = effective(p);
        return (
          <select
            className="pat-input"
            value={row.measurementType || 'percentage'}
            onChange={e => markDirty(p._id, { measurementType: e.target.value as MeasurementType })}
            aria-label={`Measurement type for ${p.name}`}
            style={{ minWidth: '120px' }}
          >
            <option value="percentage">Percentage</option>
            <option value="level">Level</option>
            <option value="count">Count</option>
          </select>
        );
      },
    },

    {
      key: 'actions',
      label: t('system.pests.columns.actions'),
      render: p => (
        <Menu
          trigger={
            <button
              type="button"
              className="pat-menu__trigger"
              aria-label={t('system.pests.columns.actions')}
            >
              ⋮
            </button>
          }
          placement="bottom-end"
        >
          <MenuItem onClick={() => handleOpenEdit(p)}>
            Edit
          </MenuItem>
          <MenuItem onClick={() => handleDelete(p._id)} tone="danger">
            Delete
          </MenuItem>
        </Menu>
      ),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      <PageHeader
        title={t('system.pests.title')}
        meta={t('system.pests.metaCount', { count: pests.length })}
        actions={
          <>
            <button
              type="button"
              className={`pat-btn ${selectionMode ? 'pat-btn--primary' : 'pat-btn--subtle'}`}
              onClick={toggleSelectionMode}
              aria-pressed={selectionMode}
            >
              {selectionMode ? 'Cancel selection' : t('system.pests.selectionMode')}
            </button>
            {selectionMode && selectedIds.size > 0 && (
              <button
                type="button"
                className="pat-btn pat-btn--danger"
                onClick={handleBulkDelete}
              >
                {t('system.pests.bulkDelete')} ({selectedIds.size})
              </button>
            )}
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={() => setShowPromoteModal(true)}
            >
              {t('system.pests.promoteButton')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--primary"
              onClick={handleOpenCreate}
            >
              {t('system.pests.newButton')}
            </button>
          </>
        }
      />

      {error && (
        <Notice tone="danger" action={<button type="button" className="pat-btn pat-btn--subtle" onClick={() => setError(null)} aria-label="Close">×</button>}>
          <span>{error}</span>
        </Notice>
      )}

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('system.pests.searchPlaceholder')}
      />

      <DataTable<GlobalPest>
        columns={columns}
        rows={filtered}
        rowKey={r => r._id}
        rowClassName={p => {
          const isDirty = !!dirtyPests[p._id];
          const isSelected = selectedIds.has(p._id);
          return [
            isDirty ? 'table-warning' : '',
            isSelected ? 'table-info' : '',
          ]
            .filter(Boolean)
            .join(' ');
        }}
        rowProps={p => ({
          onClick: selectionMode ? () => toggleSelect(p._id) : undefined,
          style: { cursor: selectionMode ? 'pointer' : undefined },
        })}
        emptyState={t('system.pests.empty')}
      />

      {hasDirtyChanges && (
        <SaveBar
          status={t('system.pests.saveBarStatus', { count: Object.keys(dirtyPests).length })}
          primaryAction={{
            label: t('system.pests.saveAll'),
            onClick: handleSaveAll,
          }}
          secondaryActions={[
            {
              label: t('system.pests.discard'),
              onClick: handleDiscard,
              tone: 'subtle',
            },
          ]}
        />
      )}

      <PestEditModal
        show={showEditModal}
        pest={editing}
        onHide={handleCloseEdit}
        onSaved={fetchGlobalPests}
      />

      <PromoteFromTenantModal
        show={showPromoteModal}
        tenants={tenants}
        onHide={() => setShowPromoteModal(false)}
        onPromoted={fetchGlobalPests}
      />
    </>
  );
};

export default SystemPestsPage;
